import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Classes from './Services.module.css';
import { CarFrontIcon, GasPumpIcon, TickIcon } from '../../../../../utils/Icons';
import { styled } from "@mui/material/styles";
import { Switch, FormControlLabel } from "@mui/material";
import { motion, AnimatePresence, easeOut } from 'framer-motion';
import { useNavigate, createSearchParams } from 'react-router-dom';
import OutOfFillPopup from '../../../../GlobalComponents/OutOfFillPopup/OutOfFillPopup';
import { useSelector } from 'react-redux';
import backend from '../../../../../api';

const PrimarySwitch = styled(Switch)({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "var(--secondary-color)",
    "&:hover": { backgroundColor: "rgba(var(--secondary-color), .08)" },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "var(--secondary-color)",
  },
});

const TYPE_PARAM_MAP = {
  regular: 'regular',
  mid: 'Diesel',
  premium: 'premium',
};
const DEFAULT_BOOKING_PAUSE_LABEL = 'Service Update';
const DEFAULT_BOOKING_PAUSE_MESSAGE =
  "Dear customers, due to military obligations of the owner, we are pausing services until summer of 2027.";

// ---------- Helpers ----------
function isSubscriptionActive(as) {
  if (!as) return false;
  const status = (as.status || 'none').toLowerCase();
  if (status === 'none' || status === 'canceled') return false;
  const now = Date.now();
  const startOk = as.current_period_start ? new Date(as.current_period_start).getTime() <= now : true;
  const endOk = as.current_period_end ? new Date(as.current_period_end).getTime() >= now : false;
  return startOk && endOk; // active/trialing/past_due but still within period
}

function getRemainingFillups(as) {
  const limit = Number(as?.plan?.fillupsLimit || 0); // 0 = unlimited
  const used = Number(as?.usedThisPeriod || 0);
  if (limit <= 0) return Infinity; // unlimited
  return Math.max(0, limit - used);
}

export default function Services() {
  const navigateTo = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const onboarding = useSelector((state) => state.companyInfo.info);
  const companyId = onboarding?._id;

  const activeSub = user?.activeSubscription || null;
  const isSubbed = isSubscriptionActive(activeSub);
  const remainingFillups = getRemainingFillups(activeSub);

  const [selected, setSelected] = useState(() => new Set());
  const [service, setService] = useState(null);
  const [rush, setRush] = useState(false);
  const [bookingsPaused, setBookingsPaused] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const [pauseLabel, setPauseLabel] = useState(DEFAULT_BOOKING_PAUSE_LABEL);
  const [pauseMessage, setPauseMessage] = useState(DEFAULT_BOOKING_PAUSE_MESSAGE);

  useEffect(() => {
    (async () => {
      try {
        if (companyId) {
          const { data } = await backend.get(`/api/services/by-company/${companyId}`);
          if (data?.ok) {
            setService(data.data || null);
            const active = !!data?.servicePause?.active;
            setBookingsPaused(active);
            setShowNotice(!!(data?.servicePause?.showNotice ?? active));
            setPauseLabel(data?.servicePause?.label || DEFAULT_BOOKING_PAUSE_LABEL);
            setPauseMessage(data?.servicePause?.message || DEFAULT_BOOKING_PAUSE_MESSAGE);
          }
        }
      } catch (error) {
        console.error('Error fetching service:', error);
      }
    })();
  }, [companyId]);

  // Delivery fee rule:
  // - Subscribed & within period  => $0.00
  // - Otherwise                   => configured fee (fallback 9.99)

  const isNewUser = useMemo(() => {
  if (!user?.createdAt) return false;
  const createdAtMs = new Date(user.createdAt).getTime();
  if (!Number.isFinite(createdAtMs)) return false;
  return (Date.now() - createdAtMs) < (24 * 60 * 60 * 1000);
}, [user?.createdAt]);

const freeDelivery = isNewUser || isSubbed;

  const baseDeliveryFee = useMemo(
  () => Number(service?.fees?.delivery ?? service?.prices?.deliveryFee ?? 9.99),
  [service]
);

const deliveryFeeForUser = useMemo(
  () => (freeDelivery ? 0 : baseDeliveryFee),
  [freeDelivery, baseDeliveryFee]
);

  const emergencyFee = useMemo(
    () => Number(service?.prices?.emergencyFee ?? service?.fees?.emergency ?? 50),
    [service]
  );

  const deliveryFeeText = useMemo(() => {
  const fee = Number(deliveryFeeForUser || 0);
  if (fee > 0) return `+ $${fee.toFixed(2)} Delivery Fee / fill-up`;
  // Free case (new user or subscription)
  return isNewUser ? '(Free delivery for 24 hours)' : '(Delivery fee waived)';
}, [deliveryFeeForUser, isNewUser]);


const priceLabel = useCallback(
  (perGallon) => {
    const valid = typeof perGallon === 'number' && !Number.isNaN(perGallon);
    const base = `$${(valid ? perGallon : 0).toFixed(2)}/Gallon`;
    const fee = Number(deliveryFeeForUser || 0);
    return fee > 0 ? `${base} ${deliveryFeeText}` : `${base} ${deliveryFeeText}`;
  },
  [deliveryFeeForUser, deliveryFeeText]
);

  const options = useMemo(() => {
    const reg = Number(service?.prices?.regular ?? 4.5);
    const mid = Number(service?.prices?.mid ?? 5.0);
    const prem = Number(service?.prices?.premium ?? 5.5);
    const desc = 'Fuel up with mobile delivery';
    return [
      { id: 'regular', title: 'Regular (87 Octane)', desc, price: priceLabel(reg) },
      { id: 'mid', title: 'Diesel ', desc, price: priceLabel(mid) },
      { id: 'premium', title: 'Premium (91-93 Octane)', desc, price: priceLabel(prem) },
    ];
  }, [service, priceLabel]);

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleKeyDown = (e, id) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleSelect(id);
    }
  };

  const selectedOptions = options.filter(o => selected.has(o.id));

  // --- OutOfFill handling + navigate ---
  const [openOutOfFillPopup, setopenOutOfFillPopup] = useState(false);
  const handleCloseOutOfFillPopup = () => setopenOutOfFillPopup(false);
  const handleOutOfFillSubmit = () => {
    setopenOutOfFillPopup(false);
    navigateTo('/settings/membership');
  };

  const handleContinue = () => {
    if (bookingsPaused) {
      window.alert(`${pauseLabel}: ${pauseMessage}`);
      return;
    }

    // If plan has limit and user has 0 remaining, show popup instead of continuing
    if (remainingFillups !== Infinity && remainingFillups <= 0) {
      setopenOutOfFillPopup(true);
      return;
    }
    const types = Array.from(selected).map(id => TYPE_PARAM_MAP[id] ?? id);
    const entries = types.map(t => ['type', t]);
    if (rush) entries.push(['rush', '1']);
    const search = createSearchParams(entries).toString();
    navigateTo({ pathname: '/add-appointment', search });
  };

  return (
    <>
      <div className={Classes.hero}>
        <div className={Classes.header}>
          <div>
            <h3 className={Classes.title3}>Services</h3>
            <h5 className={Classes.text}>Browse and purchase services.</h5>
            {showNotice ? (
              <h5 className={Classes.text} style={{ marginTop: 8, color: '#9a3412', fontWeight: 700 }}>
                <strong>{pauseLabel}:</strong> {pauseMessage}
              </h5>
            ) : null}
          </div>
          <div />
        </div>

        <div className={Classes.hero2}>
          <div className={Classes.bodyLeft}>
            <div className={Classes.boxHeader}>
              <GasPumpIcon fill={'var(--primary-color)'} height="25" />
              <h4>Mobile Fuel Delivery</h4>
            </div>

            <div className={Classes.boxesCon}>
              {options.map(opt => {
                const isActive = selected.has(opt.id);
                return (
                  <div
                    key={opt.id}
                    className={`${Classes.box} ${isActive ? Classes.activeBox : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleSelect(opt.id)}
                    onKeyDown={(e) => handleKeyDown(e, opt.id)}
                    aria-pressed={isActive}
                  >
                    <h5 className={Classes.title1}>{opt.title}</h5>
                    <h5 className={Classes.text}>{opt.desc}</h5>
                    <h5 className={Classes.title2}>{opt.price}</h5>

                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.div
                          key="tick"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0 }}
                          transition={{ duration: 0.2, ease: easeOut }}
                          className={Classes.tick}
                        >
                          <TickIcon fill="white" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            <div className={Classes.toggleCon}>
              <FormControlLabel
                control={
                  <PrimarySwitch
                    checked={rush}
                    onChange={(e) => setRush(e.target.checked)}
                    sx={{ m: 0 }}
                  />
                }
                sx={{ m: 0, gap: 8, '.MuiFormControlLabel-label': { m: 0 } }}
              />
              <h5>
                Add Emergency Delivery <span>+${emergencyFee.toFixed(0)}</span>
              </h5>
            </div>

            <div className={Classes.detailCon}>
              <CarFrontIcon fill={'var(--primary-color)'} height="25" />
              <h5>
                Mobile Car Detailing <span> Coming Soon </span>
              </h5>
            </div>

            <div
              className={Classes.submitBtn}
              onClick={() => !bookingsPaused && handleContinue()}
              role="button"
              tabIndex={bookingsPaused ? -1 : 0}
              onKeyDown={(e) =>
                !bookingsPaused && (e.key === 'Enter' || e.key === ' ') && handleContinue()
              }
              style={bookingsPaused ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
              aria-disabled={bookingsPaused}
            >
              <h5>{bookingsPaused ? 'Booking Paused' : 'Continue'}</h5>
            </div>
          </div>

          <div className={Classes.bodyRight}>
            <h4 className={Classes.title4}>Summary</h4>

            <div className={Classes.summaryBody}>
              {selectedOptions.length === 0 ? (
                <h5 className={Classes.text}>No services selected...</h5>
              ) : (
                <>
                  {selectedOptions.map((o) => (
                    <div key={o.id} className={Classes.item}>
                      <h5>{o.title}</h5>
                      <h5 className={Classes.textMini}>{o.price}</h5>
                    </div>
                  ))}
                  <div className={Classes.item}>
  <div>
    <h5>Delivery Fee</h5>
    <h5 className={Classes.textMini} style={{ opacity: 0.8 }}>
      {deliveryFeeText}
    </h5>
  </div>
  <h5 className={Classes.textMini}>${deliveryFeeForUser.toFixed(2)}</h5>
</div>

                  {rush && (
                    <div className={Classes.item}>
                      <h5>Emergency Fee</h5>
                      <h5 className={Classes.textMini}>${emergencyFee.toFixed(2)}</h5>
                    </div>
                  )}
                </>
              )}

              <input type="text" placeholder="Have a coupon?" className={Classes.couponInput} />

              <div className={Classes.item}>
                <div>
                  <h5>Total</h5>
                  <h5 className={Classes.textMini}>USD</h5>
                </div>
                <h5>TBD</h5>
              </div>
            </div>

            {/* Optional mini-hint */}
            <div className={Classes.text} style={{ marginTop: 8 }}>
              {isSubbed ||isNewUser
                ? 'Delivery fee waived.'
                : 'No active membership: $9.99 delivery fee applies.'}
            </div>
            {remainingFillups !== Infinity && (
              <div className={Classes.text} style={{ marginTop: 4 }}>
                Fill-ups remaining this period: <strong>{remainingFillups}</strong>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {openOutOfFillPopup && (
          <OutOfFillPopup
            handleClose={handleCloseOutOfFillPopup}
            handleSubmit={handleOutOfFillSubmit}
          />
        )}
      </AnimatePresence>
    </>
  );
}
